// src/components/DistrictDescription.jsx
import React from 'react';
import styled from 'styled-components';

const DescriptionWrap = styled.div`
  margin-top: 40px;
`;

const DescriptionContent = styled.div`
  line-height: 1.8;
  font-size: 17px;
  color: var(--fg);
  
  h1, h2, h3, h4, h5, h6 {
    margin: 32px 0 20px;
    color: var(--fg);
    font-weight: 700;
    line-height: 1.3;
  }
  
  h2 {
    font-size: 28px;
    border-bottom: 3px solid var(--primary);
    padding-bottom: 12px;
    margin-top: 40px;
  }
  
  h3 {
    font-size: 22px;
    color: var(--primary);
    margin-top: 28px;
  }
  
  h4 {
    font-size: 19px;
    margin-top: 24px;
  }
  
  p {
    margin: 0 0 20px;
    color: var(--fg);
    text-align: justify;
    text-indent: 0;
    
    /* Улучшенная типографика */
    font-weight: 400;
    letter-spacing: 0.01em;
    
    /* Первый абзац без отступа */
    &:first-of-type {
      margin-top: 0;
    }
  }
  
  ul, ol {
    margin: 0 0 20px 28px;
    color: var(--fg);
    
    li {
      margin-bottom: 12px;
      line-height: 1.7;
      padding-left: 8px;
    }
  }
  
  ul li {
    list-style-type: disc;
  }
  
  ol li {
    list-style-type: decimal;
  }
  
  blockquote {
    margin: 28px 0;
    padding: 20px 24px;
    border-left: 5px solid var(--primary);
    background: rgba(176, 141, 87, 0.08);
    border-radius: 0 12px 12px 0;
    font-style: italic;
    font-size: 16px;
    
    p {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: 700;
    color: var(--primary);
  }
  
  em, i {
    font-style: italic;
    color: var(--muted);
  }
  
  /* Улучшаем отображение ссылок */
  a {
    color: var(--primary);
    text-decoration: underline;
    transition: color 0.2s ease;
    font-weight: 500;
    
    &:hover {
      color: var(--fg);
      text-decoration: none;
    }
  }
  
  /* Стилизация таблиц если есть */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    th, td {
      padding: 16px;
      border: 1px solid var(--bdr);
      text-align: left;
    }
    
    th {
      background: var(--primary);
      color: var(--primaryFg);
      font-weight: 600;
    }
    
    tr:nth-child(even) {
      background: rgba(176, 141, 87, 0.05);
    }
  }
  
  /* Стилизация изображений */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  /* Стилизация кода */
  code {
    background: rgba(176, 141, 87, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 14px;
  }
  
  pre {
    background: rgba(176, 141, 87, 0.05);
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 20px 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
`;

const CategorySection = styled.div`
  margin: 32px 0;
`;

const CategoryHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border: 1px solid var(--bdr);
  border-radius: 12px;
  background: var(--card);
  color: var(--fg);
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary);
    color: var(--primaryFg);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .icon {
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const CategoryContent = styled.div`
  margin-top: 12px;
  padding: 20px;
  border: 1px solid var(--bdr);
  border-radius: 12px;
  background: var(--card);
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  .category-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }
  
  .place-item {
    padding: 12px;
    border: 1px solid var(--bdr);
    border-radius: 8px;
    background: rgba(176, 141, 87, 0.05);
    
    .name {
      font-weight: 600;
      margin-bottom: 4px;
    }
    
    .description {
      font-size: 14px;
      color: var(--muted);
      line-height: 1.4;
    }
    
    .distance {
      font-size: 12px;
      color: var(--primary);
      font-weight: 600;
      margin-top: 4px;
    }
  }
`;

const ChevronIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
  </svg>
);

export default function DistrictDescription({ 
  district, 
  language = 'ru',
  categories = {}
}) {
  // Инфраструктура открыта по умолчанию
  const [openCategories, setOpenCategories] = React.useState({
    restaurants: true,
    shopping: true,
    culture: true,
    entertainment: true,
    education: true,
    healthcare: true,
    transport: true,
    parks: true
  });
  
  const toggleCategory = (categoryKey) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };
  
  const categoryLabels = {
    restaurants: language === 'en' ? 'Restaurants' : 'Рестораны',
    shopping: language === 'en' ? 'Shopping Centers' : 'Торговые центры',
    culture: language === 'en' ? 'Cultural Objects' : 'Объекты культуры',
    entertainment: language === 'en' ? 'Entertainment' : 'Объекты досуга',
    education: language === 'en' ? 'Education' : 'Образование',
    healthcare: language === 'en' ? 'Healthcare' : 'Медицина',
    transport: language === 'en' ? 'Transport' : 'Транспорт',
    parks: language === 'en' ? 'Parks & Recreation' : 'Парки и отдых'
  };
  
  // Тестовые данные для категорий
  const defaultCategories = {
    restaurants: [
      {
        name: 'Nobu Dubai',
        description: 'Японская кухня премиум-класса с видом на море',
        distance: '0.5 км'
      },
      {
        name: 'Zuma',
        description: 'Современная японская кухня в стильном интерьере',
        distance: '0.8 км'
      },
      {
        name: 'La Petite Maison',
        description: 'Французская кухня в элегантной обстановке',
        distance: '1.2 км'
      }
    ],
    shopping: [
      {
        name: 'Dubai Mall',
        description: 'Крупнейший торговый центр с более чем 1200 магазинами',
        distance: '2.1 км'
      },
      {
        name: 'Mall of the Emirates',
        description: 'Премиальный шопинг с крытым горнолыжным склоном',
        distance: '3.5 км'
      },
      {
        name: 'City Walk',
        description: 'Открытый торговый район с бутиками и ресторанами',
        distance: '1.8 км'
      }
    ],
    culture: [
      {
        name: 'Dubai Opera',
        description: 'Главная оперная площадка города',
        distance: '1.5 км'
      },
      {
        name: 'Dubai Museum',
        description: 'Музей истории и культуры Дубая',
        distance: '2.3 км'
      }
    ],
    entertainment: [
      {
        name: 'Burj Khalifa',
        description: 'Самое высокое здание в мире с обзорными площадками',
        distance: '2.0 км'
      },
      {
        name: 'Dubai Fountain',
        description: 'Музыкальные фонтаны у подножия Burj Khalifa',
        distance: '2.1 км'
      },
      {
        name: 'Atlantis Aquaventure',
        description: 'Аквапарк мирового класса',
        distance: '4.2 км'
      }
    ]
  };
  
  const allCategories = { ...defaultCategories, ...categories };
  
  const description = district?.description || 
    district?.description_ru || 
    district?.full_description ||
    `${String(district?.name || 'Район')} — это престижный район с развитой инфраструктурой и удобным расположением. Здесь сочетаются современная архитектура, зеленые зоны и близость к основным достопримечательностям города.`;
  
  return (
    <DescriptionWrap id="description">
      <h2>{language === 'en' ? 'About the District' : 'Описание района'}</h2>
      
      <DescriptionContent>
        <div dangerouslySetInnerHTML={{ __html: description }} />
      </DescriptionContent>
      
      {Object.keys(allCategories).length > 0 && (
        <div>
          <h3 style={{ marginTop: '32px', marginBottom: '20px' }}>
            {language === 'en' ? 'Infrastructure and Amenities' : 'Инфраструктура и удобства'}
          </h3>
          
          {Object.entries(allCategories).map(([categoryKey, places]) => {
            if (!places || !places.length) return null;
            
            const isOpen = openCategories[categoryKey];
            
            return (
              <CategorySection key={categoryKey}>
                <CategoryHeader
                  isOpen={isOpen}
                  onClick={() => toggleCategory(categoryKey)}
                >
                  <span>{categoryLabels[categoryKey] || categoryKey}</span>
                  <span className="icon">
                    <ChevronIcon />
                  </span>
                </CategoryHeader>
                
                <CategoryContent isOpen={isOpen}>
                  <div className="category-grid">
                    {places.map((place, index) => (
                      <div key={index} className="place-item">
                        <div className="name">{String(place.name || '')}</div>
                        {place.description && (
                          <div className="description">{String(place.description)}</div>
                        )}
                        {place.distance && (
                          <div className="distance">{String(place.distance)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </CategoryContent>
              </CategorySection>
            );
          })}
        </div>
      )}
    </DescriptionWrap>
  );
}